import { zodResolver } from '@hookform/resolvers/zod';
import { useListTenants } from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  cn,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Stepper,
} from '@sunstone/ui';
import { ArrowLeft, ChevronRight, Info, Loader2, Server } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { useAuthToken } from '@/features/auth';
import { VERSION_UNKNOWN_LABEL } from '@/features/workspaces/WorkspaceVersionStatus';
import { useDebounce } from '@/hooks/useDebounce';
import {
  type DuploAiTenant,
  MOCK_DUPLOAI_WORKSPACES,
  onPremInfras,
  tenantsForInfra,
} from '@/lib/mock/duploai';
import { routes } from '@/lib/routes';
import { formatDateTime } from '@/lib/time';

// ─── Wizard ───────────────────────────────────────────────────────────────────

const WIZARD_STEPS = ['Connect', 'Clusters', 'Review'];

const DEFAULT_BASE_URL = 'https://ops-helpdesk.baic-dev.uniphorecloud.com';

/**
 * No API token field by design. Sunstone reads Duplo with one stored service
 * credential per environment, not a token pasted per import: a pasted portal token
 * expires in two hours, which would silently stop version reads, and it would tie
 * a long-lived integration to whichever operator happened to run the wizard.
 */
const connectSchema = z.object({
  baseUrl: z.string().url('Enter a valid URL'),
  customerId: z.string().min(1, 'Select the customer for this deployment'),
});

type ConnectValues = z.infer<typeof connectSchema>;

// ─── Indicators ───────────────────────────────────────────────────────────────

/**
 * Terraform's reported status plus why it says that. The badge alone ("Failed")
 * doesn't distinguish a one-off from a cluster that has never come up, so the
 * detail line carries the consecutive-failure run and when the last run finished.
 *
 * Consecutive failures, not total: dh-uae has 37 failures across 138 actions and
 * is currently healthy, so a total would read as alarming when nothing is wrong.
 *
 * The action type matters too — a failed destroy is a different problem from a
 * failed apply, and agent-sbox's last action was a destroy.
 */
function TerraformStatus({ tenant }: { tenant: DuploAiTenant }) {
  const tone =
    tenant.status === 'Complete'
      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      : tenant.status === 'Failed'
        ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300';

  const runs = tenant.consecutiveFailures ?? 0;
  const verb = tenant.lastActionType ?? 'run';
  // "apply" pluralises to "applies", not "applys" — naive +s gets this wrong.
  const PLURAL: Record<string, string> = {
    apply: 'applies',
    plan: 'plans',
    destroy: 'destroys',
    run: 'runs',
  };
  const detail = [
    runs > 0
      ? `${runs} failed ${runs === 1 ? verb : (PLURAL[verb] ?? `${verb}s`)}`
      : `last ${verb} succeeded`,
    tenant.lastActionAt ? formatDateTime(new Date(tenant.lastActionAt)) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-col gap-1">
      <Badge className={cn(tone, 'w-fit')}>{tenant.status}</Badge>
      {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

/**
 * The version actually deployed, or the shared unknown label. Never falls back to
 * the desired version — reporting a cluster as running a version it may not be
 * running is worse than reporting nothing.
 */
function DeployedVersion({ tenant, className }: { tenant: DuploAiTenant; className?: string }) {
  return tenant.deployedChartVersion ? (
    <p className={cn('text-sm font-mono text-foreground', className)}>
      {tenant.deployedChartVersion}
    </p>
  ) : (
    <p className={cn('text-sm text-muted-foreground', className)}>{VERSION_UNKNOWN_LABEL}</p>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * Imports an already-provisioned on-prem cluster as a Sunstone deployment.
 *
 * The cluster and its BAIC tenants exist in Duplo before Sunstone hears about them,
 * so this is a discover-and-select flow rather than a create flow: Duplo infra →
 * Sunstone deployment, Duplo BAIC tenant → Sunstone workspace. Nothing here
 * provisions or mutates infrastructure.
 *
 * There is no Duplo-workspace picker: on-prem clusters all live in one workspace
 * (in baic-dev, `xforge-installer` holds every BAIC resource while `DevOps` and
 * `monitoring` hold none), so asking an operator to choose adds a way to be wrong
 * and no information. Discovery searches all workspaces and reports where it looked.
 *
 * Currently fixture-backed. The discovery calls and the register RPC do not exist
 * yet, so the final action reports what it would do rather than doing it.
 */
export function ImportOnPremClusterPage() {
  const navigate = useNavigate();
  const { token } = useAuthToken();

  const [step, setStep] = useState(1);
  const [selectedInfra, setSelectedInfra] = useState('');
  const [connecting, setConnecting] = useState(false);

  const [customerSearch, setCustomerSearch] = useState('');
  const debouncedCustomerSearch = useDebounce(customerSearch, 300);
  const { data: customersData } = useListTenants(token, undefined, {
    search: debouncedCustomerSearch,
    pageSize: 20,
  });
  const customers = customersData?.tenants ?? [];

  const form = useForm<ConnectValues>({
    resolver: zodResolver(connectSchema),
    defaultValues: { baseUrl: DEFAULT_BASE_URL, customerId: '' },
    mode: 'onChange',
  });

  const clusters = useMemo(() => onPremInfras(), []);
  const importedTenants = useMemo(
    () => (selectedInfra ? tenantsForInfra(selectedInfra) : []),
    [selectedInfra],
  );
  const selectedCustomerId = form.watch('customerId');
  const customerName = customers.find((c) => c.id === selectedCustomerId)?.displayName ?? '';

  async function handleConnect() {
    const valid = await form.trigger();
    if (!valid) return;
    setConnecting(true);
    // Stands in for listing workspaces and their BAIC infras.
    setTimeout(() => {
      setConnecting(false);
      setStep(2);
    }, 400);
  }

  function handleImport() {
    toast.info('Preview only — no deployment was created.', {
      description: `Would register ${selectedInfra} with ${importedTenants.length} workspace${
        importedTenants.length === 1 ? '' : 's'
      } under ${customerName || 'the selected customer'}.`,
    });
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="Import On-Prem Cluster"
        description="Register a cluster that Duplo already manages. Sunstone reads its version and health; it does not provision or deploy."
      />

      <Stepper steps={WIZARD_STEPS} current={step} className="mb-8" />

      {/* Step 1 · Connect */}
      {step === 1 && (
        <Card>
          <CardContent className="py-6 space-y-6">
            <Form {...form}>
              <form className="space-y-6">
                <FormField
                  control={form.control}
                  name="baseUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duplo AI base URL</FormLabel>
                      <FormControl>
                        <Input placeholder={DEFAULT_BASE_URL} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="customerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer</FormLabel>
                      <FormControl>
                        <SearchableCombobox
                          value={field.value}
                          onChange={(val) => {
                            field.onChange(val);
                            setCustomerSearch('');
                          }}
                          items={customers}
                          search={customerSearch}
                          onSearchChange={setCustomerSearch}
                          placeholder="Select a customer…"
                          searchPlaceholder="Search customers…"
                          emptyMessage="No customers found."
                        />
                      </FormControl>
                      <FormDescription>
                        The imported cluster becomes a deployment under this customer.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>

            <div className="flex justify-end">
              <Button onClick={handleConnect} disabled={connecting}>
                {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
                Find clusters
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2 · Clusters */}
      {step === 2 && (
        <Card>
          <CardContent className="py-6 space-y-6">
            {clusters.length === 0 ? (
              <EmptyState
                icon={<Server className="h-8 w-8 text-muted-foreground" />}
                title="No on-prem clusters found"
                description="Nothing in this Duplo portal reports an on-prem cloud provider."
              />
            ) : (
              <>
                <div className="space-y-1">
                  <h3 className="text-sm font-medium">On-prem clusters found</h3>
                  <p className="text-sm text-muted-foreground">
                    Each cluster becomes one deployment.
                  </p>
                </div>

                {/* Card-style toggle: no @sunstone/ui ToggleGroup equivalent yet */}
                <div className="grid gap-3 sm:grid-cols-2">
                  {clusters.map((cluster) => {
                    const count = tenantsForInfra(cluster.name).length;
                    const selected = selectedInfra === cluster.name;
                    return (
                      <button
                        key={cluster.id}
                        type="button"
                        onClick={() => setSelectedInfra(selected ? '' : cluster.name)}
                        className={cn(
                          'flex flex-col items-start gap-1 rounded-lg border px-4 py-4 text-left transition-all',
                          selected
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                        )}
                      >
                        <span className="text-sm font-semibold">{cluster.name}</span>
                        <span className="text-xs leading-relaxed">
                          {cluster.cloudProvider} · {cluster.status} · {count} workspace
                          {count === 1 ? '' : 's'}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <p className="text-sm text-muted-foreground">
                  Searched {MOCK_DUPLOAI_WORKSPACES.length} Duplo AI workspaces. Only on-prem
                  clusters can be imported here — AWS and Azure clusters are managed elsewhere.
                </p>
              </>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!selectedInfra}>
                Review
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 · Review */}
      {step === 3 && (
        <div className="space-y-6">
          {/* Deployment metadata — same shape as the workspace detail card */}
          <Card>
            <CardContent className="py-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Cluster</p>
                  <p className="text-sm font-mono text-foreground">{selectedInfra}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Customer</p>
                  <p className="text-sm text-foreground">{customerName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Type</p>
                  <p className="text-sm text-foreground">On-Prem · Hybrid</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Status On Import</p>
                  <p className="text-sm text-foreground">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* One card per workspace, laid out like WorkspaceDetail's metadata card */}
          {importedTenants.map((tenant) => (
            <Card key={tenant.id}>
              <CardContent className="py-4">
                <p className="text-sm font-medium text-foreground mb-4">Workspace</p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 font-medium">URL Name</p>
                    <p className="text-sm font-mono text-foreground">{tenant.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 font-medium">
                      Deployed Version
                    </p>
                    <DeployedVersion tenant={tenant} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 font-medium">Terraform</p>
                    <TerraformStatus tenant={tenant} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <Alert className="border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100">
            <Info className="h-4 w-4" />
            <AlertDescription>Preview only — nothing is saved yet.</AlertDescription>
          </Alert>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(2)}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(routes.deployments())}>
                Cancel
              </Button>
              <Button onClick={handleImport}>Import cluster</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
